import { useState } from "react";
import { api, ApiError } from "../api.ts";
import { AlertBanner } from "../components/ui.tsx";
import { useAsync } from "../hooks.ts";

/**
 * Why a sign-in did not finish, in words.
 *
 * `/auth/callback` sends the browser back here with `login_error` rather than
 * printing a JSON body into the address bar. Anything unrecognised falls
 * through to the raw code plus whatever detail came with it — an unexplained
 * failure is worse than an ugly one.
 */
function loginErrorMessage(code: string, detail: string | null): string {
  switch (code) {
    case "login_expired":
      return "That sign-in took too long, or started in a different browser. Try again.";
    case "bad_state":
      return "That sign-in response didn’t match the one this browser started. Try again.";
    case "no_code":
      return "authentik came back without an authorisation code. Try again.";
    case "not_a_member":
      return `You signed in, but you’re not in the ${detail ?? "parents"} group yet. Ask Gary or Keren to add you in authentik — as a parent if you live here, or a guest if you’re staying.`;
    case "provider_invalid_request":
      return "authentik refused the sign-in request as malformed — the chores application is misconfigured, not you.";
    case "provider_access_denied":
      return "Sign-in was cancelled or refused.";
    default:
      return detail ? `Sign-in failed: ${code} — ${detail}` : `Sign-in failed: ${code}`;
  }
}

/**
 * Two doors, and only one of them may exist.
 *
 * Everyone with an authentik account goes through the same button — the groups
 * they are in decide whether they arrive as a parent, as a guest staying with
 * us, or not at all. Kids instead pick their face and type the household PIN on
 * the shared tablet. With KID_PIN unset there is no kid door, so the page does
 * not draw one.
 */
export function Login({ kidMode, onSignedIn }: { kidMode: boolean; onSignedIn: () => void }) {
  const kids = useAsync(() => api.people());
  const [selected, setSelected] = useState<number | null>(null);
  const [pin, setPin] = useState("");
  const params = new URLSearchParams(window.location.search);
  const loginError = params.get("login_error");
  const [error, setError] = useState<string | null>(
    loginError ? loginErrorMessage(loginError, params.get("login_detail")) : null,
  );

  async function submitKid(event: React.FormEvent) {
    event.preventDefault();
    if (selected === null) return;
    try {
      await api.kidLogin(pin, selected);
      onSignedIn();
    } catch (cause) {
      const code = cause instanceof ApiError ? cause.code : "";
      setError(
        code === "kid_mode_disabled"
          ? "Kid sign-in is switched off."
          : code === "no_pin_set"
            ? "No PIN set for them yet — a parent can set one in the admin page."
            : code === "too_many_tries"
              ? "Too many tries. Wait a few minutes and try again."
              : "That PIN isn’t right.",
      );
      setPin("");
    }
  }

  return (
    <main className="signin">
      <div className="signin__mark" aria-hidden="true" />
      <div>
        <h1 className="signin__title">Chores</h1>
        <span className="eyebrow">Who’s this?</span>
      </div>

      <div className="signin__panel">
        {error ? <AlertBanner>{error}</AlertBanner> : null}

        <a className="btn btn--primary" href="/auth/login" style={{ width: "100%" }}>
          Sign in
        </a>

        {kidMode && (kids.data ?? []).length > 0 ? (
          <div className="panel" style={{ marginTop: "var(--space-5)", textAlign: "left" }}>
            <span className="eyebrow">Kids</span>
            <div className="crew" style={{ marginTop: "var(--space-3)", marginBottom: "var(--space-4)" }}>
              {(kids.data ?? []).map((person) => (
                <button
                  key={person.id}
                  type="button"
                  className="crew__person"
                  aria-pressed={selected === person.id}
                  onClick={() => setSelected(person.id)}
                >
                  <span className="crew__face" aria-hidden="true">
                    {person.emoji}
                  </span>
                  {person.name}
                </button>
              ))}
            </div>
            <form onSubmit={submitKid} className="btn-row" style={{ alignItems: "flex-end" }}>
              <label className="field" style={{ flex: 1 }}>
                <span className="field__label">PIN</span>
                <input
                  value={pin}
                  onChange={(event) => setPin(event.target.value)}
                  inputMode="numeric"
                  autoComplete="off"
                  type="password"
                />
              </label>
              <button className="btn btn--ghost" type="submit" disabled={selected === null || pin.length === 0}>
                Go
              </button>
            </form>
          </div>
        ) : null}
      </div>
    </main>
  );
}
